import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import FilterPanel from './FilterPanel';
import { getInitialFilters, getFilterConfigByRole, filtersToApiParams } from '../config/filterConfig';

// 用户头像图标 (SVG)
const UserIcon = () => (
    <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
    </svg>
);

// 搜索图标 (SVG)
const SearchIcon = () => (
    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
    </svg>
);

export default function TalentSelectorModal({ isOpen, onClose, onSelect }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [talents, setTalents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [show, setShow] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(20);

    // 筛选状态
    const [filters, setFilters] = useState(() => {
        const config = getFilterConfigByRole('Accounting');
        return getInitialFilters(config);
    });

    const fetchTalents = useCallback(async (page = 1) => {
        setIsLoading(true);
        try {
            // 转换筛选为API参数
            const filterParams = filtersToApiParams(filters);

            const response = await apiService.getTalents({
                searchTerm: searchTerm,
                page: page,
                pageSize: pageSize,
                ...filterParams, // 合并筛选参数
            });
            
            // 处理分页响应数据
            const data = response.data || response;
            setTotalPages(response.totalPages || 1);
            setTotalCount(response.totalCount || data.length);
            setCurrentPage(response.currentPage || page);
            
            const isResidence = user?.role === 'Residence';
            const isAccounting = user?.role === 'Accounting';
            
            // 🔍 调试：打印后端返回的数据结构
            if (data && data.length > 0) {
                console.log('TalentSelectorModal - Sample talent data:', data[0]);
                console.log('User role:', user?.role);
            }
            
            // ✅ 性能优化：使用后端返回的状态数据，避免N+1查询
            const talentsWithStatus = data.map(talent => {
                if (isResidence) {
                    const isResidenceEdited = talent.isResidenceEdited ?? talent.IsResidenceEdited;
                    return { 
                        ...talent, 
                        hasResidenceData: isResidenceEdited ?? talent.hasResidenceData ?? talent.HasResidenceData ?? false,
                        statusType: 'residence' 
                    };
                } else if (isAccounting) {
                    return { 
                        ...talent, 
                        hasLoanData: talent.hasLoanData || talent.HasLoanData || false,
                        lastRepaymentDate: talent.lastRepaymentDate || talent.LastRepaymentDate || null,
                        isLoanFullyRepaid: talent.isLoanFullyRepaid || talent.IsLoanFullyRepaid || false,
                        statusType: 'accounting' 
                    };
                } else {
                    return { ...talent, statusType: 'other' };
                }
            });
            
            setTalents(talentsWithStatus);
        } catch (err) {
            console.error(err);
            alert(t('fetchFailed'));
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm, pageSize, filters, t, user]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => setShow(true), 10);
            setCurrentPage(1); // 重置到第一页
            fetchTalents(1);
        } else {
            setShow(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = setTimeout(() => {
            setCurrentPage(1); // 搜索时重置到第一页
            fetchTalents(1);
        }, 500); // 增加防抖时间到500ms
        return () => clearTimeout(handler);
    }, [searchTerm, isOpen, fetchTalents]);

    // 处理分页变化
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
            setCurrentPage(newPage);
            fetchTalents(newPage);
        }
    };
    
    const handleClose = () => {
        setShow(false);
        setTimeout(onClose, 300); 
    };

    if (!isOpen && !show) return null;

    return (
        <div
            className={`fixed inset-0 bg-black z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${show ? 'bg-opacity-60' : 'bg-opacity-0'}`}
            onClick={handleClose}
        >
            <div
                className={`bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex transform transition-all duration-300 ${show ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
                style={{ height: '90vh' }}
                onClick={e => e.stopPropagation()}
            >
                {/* 筛选面板 */}
                <FilterPanel
                    role="Accounting"
                    filters={filters}
                    onFiltersChange={setFilters}
                />

                {/* 主内容区域 */}
                <div className="flex-1 flex flex-col">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-2xl font-bold text-gray-800">{t('selectTalent')}</h2>
                        <p className="text-sm text-gray-500 mt-1">{t('selectTalentPrompt')}</p>
                        <div className="relative mt-4">
                            <SearchIcon />
                            <input
                                type="text"
                                placeholder={t('searchNameNumber')}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="p-3 flex-grow overflow-y-auto bg-gray-50">
                    {isLoading ? (
                        <div className="flex justify-center items-center h-full"><p className="text-gray-500 animate-pulse">{t('loading')}</p></div>
                    ) : (
                        <ul className="space-y-2">
                            {talents.length > 0 ? talents.map(talent => (
                                <li key={talent.id} className="p-3 bg-white border border-gray-200 rounded-lg flex justify-between items-center hover:shadow-md hover:border-indigo-500 transition-all duration-200">
                                    <div className="flex items-center flex-grow">
                                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                                            <UserIcon />
                                        </div>
                                        <div className="flex-grow">
                                            <p className="font-semibold text-gray-900">{talent.fullName}</p>
                                            <div className="flex items-center mt-1">
                                                {talent.statusType === 'residence' && (
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        talent.hasResidenceData 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                                                            talent.hasResidenceData ? 'bg-green-400' : 'bg-gray-400'
                                                        }`}></div>
                                                        {talent.hasResidenceData ? t('edited') : t('notEdited')}
                                                    </span>
                                                )}
                                                {talent.statusType === 'accounting' && (
                                                    <div className="flex flex-col space-y-1">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                            talent.hasLoanData 
                                                                ? 'bg-blue-100 text-blue-800' 
                                                                : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                                                                talent.hasLoanData ? 'bg-blue-400' : 'bg-gray-400'
                                                            }`}></div>
                                                            {talent.hasLoanData ? t('loanRegistered') : t('loanNotRegistered')}
                                                        </span>
                                                        {talent.hasLoanData && talent.lastRepaymentDate && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                <div className="w-1.5 h-1.5 rounded-full mr-1 bg-green-400"></div>
                                                                {t('lastRepaymentDate')}: {new Date(talent.lastRepaymentDate).toLocaleDateString('ja-JP')}
                                                            </span>
                                                        )}
                                                        {talent.hasLoanData && talent.isLoanFullyRepaid && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                <div className="w-1.5 h-1.5 rounded-full mr-1 bg-purple-400"></div>
                                                                {t('loanFullyRepaid')}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => onSelect(talent.id)} 
                                        className="text-sm bg-indigo-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-transform transform hover:scale-105 shadow-sm ml-4"
                                    >
                                        {t('view')}
                                    </button>
                                </li>
                            )) : (
                                <div className="text-center py-10 text-gray-500">
                                    <p>{t('noTalentDataFound')}</p>
                                </div>
                            )}
                        </ul>
                    )}
                    
                    {/* 分页控件 */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center space-x-2 mt-4 pb-2">
                            <button 
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1 rounded-md bg-white border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ←
                            </button>
                            
                            {/* 页码显示 */}
                            <div className="flex items-center space-x-1">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }
                                    
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                                                pageNum === currentPage
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                            
                            <button 
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 rounded-md bg-white border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                →
                            </button>
                            
                            <span className="text-xs text-gray-500 ml-4">
                                {currentPage} / {totalPages} ({totalCount} {t('totalTalents')})
                            </span>
                        </div>
                    )}
                </div>
                    <div className="bg-gray-100 px-6 py-4 flex justify-end border-t border-gray-200 rounded-b-2xl">
                        <button onClick={handleClose} className="bg-white text-gray-800 font-semibold px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition">{t('close')}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
